import { useRef, useState, useEffect } from 'react';

/**
 * VerificationCodeInput - a row of single-digit boxes for entering a login code.
 *
 * Behavior modeled on the code-test verification-code-input:
 * - auto-advances to the next box as digits are typed
 * - Backspace on an empty box moves to the previous box
 * - Left/Right arrows move between boxes
 * - pasting a full code fills every box
 * - auto-submits (onComplete) once all boxes are filled
 *
 * @param {number}   [length=6]   number of digits
 * @param {Function} onComplete   called with the full code string when all boxes are filled
 * @param {boolean}  [disabled]   disable all boxes (e.g. while verifying)
 * @param {string}   [error]      error message shown beneath the boxes
 *
 * To clear the boxes and refocus (e.g. after a rejected code), remount by changing the
 * component's `key` prop rather than passing a reset signal in.
 */
function VerificationCodeInput({ length = 6, onComplete, disabled = false, error = '' }) {
  const [code, setCode] = useState(() => Array(length).fill(''));
  const inputRefs = useRef([]);

  // Focus the first box on mount.
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const submitIfComplete = (arr) => {
    if (arr.every((d) => d !== '')) {
      onComplete(arr.join(''));
    }
  };

  // Fills every box from a multi-digit string (autofill delivering the whole code to one
  // box, or a paste) - always starting at box 0, same as a paste would.
  const distributeDigits = (digits) => {
    const next = Array(length).fill('');
    digits.slice(0, length).split('').forEach((d, i) => { next[i] = d; });
    setCode(next);
    inputRefs.current[Math.min(digits.length, length - 1)]?.focus();
    submitIfComplete(next);
  };

  const handleChange = (index, rawValue) => {
    const digits = rawValue.replace(/[^0-9]/g, '');
    if (digits.length > 1) {
      // Autofill (e.g. iOS/Android one-time-code) can deliver the full code as a single
      // value change, bypassing maxLength - treat it the same as a paste.
      distributeDigits(digits);
      return;
    }
    const next = [...code];
    // Take the last typed digit so re-typing over a filled box works.
    next[index] = digits ? digits[digits.length - 1] : '';
    setCode(next);
    if (digits && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    if (digits) {
      submitIfComplete(next);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData('text') || '').replace(/[^0-9]/g, '');
    if (!pasted) {
      return;
    }
    e.preventDefault();
    distributeDigits(pasted);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => { inputRefs.current[index] = el; }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            className="form-control"
            style={{
              width: '48px',
              height: '56px',
              padding: 0,
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: 'bold'
            }}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={(e) => e.target.select()}
            onPaste={handlePaste}
            disabled={disabled}
            aria-label={`Login code digit ${index + 1}`}
          />
        ))}
      </div>
      {error && <p className="text-danger text-center" style={{ marginTop: '12px' }}>{error}</p>}
    </div>
  );
}

export default VerificationCodeInput;
